import { Head, router, usePage } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import {
  loginPath,
  newCelebrationPath,
  profileFriendshipsPath,
  profilePath,
  profileStorePath,
  rootPath,
} from "@travily/routes";
import type CommunitiesShow from "@travily/types/pages/CommunitiesShow";
import { Alert } from "@ui/components/Alert";
import { SwooshBackground } from "@ui/components/Swoosh";
import { showToast } from "@ui/components/Toast";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { PROFILE_SWOOSH_COLORS } from "@/components/ProfileFrame/ProfileFrame";
import { useFriendshipActions } from "@/hooks/use-friendship-actions";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";
import { ShowView } from "./show.view";

const CommunityShowPage = ({
  community,
  programs,
  celebrations,
  can,
  claimed,
  support_mailto,
  has_store,
  restricted,
}: CommunitiesShow) => {
  const { share } = useShare();
  const { visitModal } = useModalStack();
  const { current_account } = usePage<SharedProps>().props;
  const isOwnProfile = current_account?.handle === community.handle;
  const onBack = useNavigateBack(rootPath());
  const friendshipActions = useFriendshipActions(community);

  const handleShareProfile = () => {
    share({
      title: community.display_name,
      text: t("web.communities.show.share_text", { name: community.display_name }),
      url: `${location.origin}${profilePath(community.handle)}`,
    });
  };

  const handleViewFriends = () => {
    if (!current_account) {
      router.visit(loginPath());
      return;
    }
    // `restricted` is a per-request snapshot, so a profile that flipped private (or got renamed) since
    // render still 404s here — uncaught, that rejection escapes to window.onunhandledrejection.
    visitModal(profileFriendshipsPath(community.handle)).catch(() =>
      showToast.error(t("common.friendships.action_error.title"), t("common.friendships.action_error.description")),
    );
  };

  return (
    <PageFrame
      background={<SwooshBackground className="hidden md:block" color={PROFILE_SWOOSH_COLORS.Community} />}
      mobileTopBar={isOwnProfile ? undefined : <TopBarMobile onBack={onBack} title={`@${community.handle}`} />}
    >
      <Head title={t("web.communities.show.meta_title", { name: community.display_name })}>
        <meta
          name="description"
          content={t("web.communities.show.meta_description", { name: community.display_name })}
        />
      </Head>
      <ShowView
        banner={
          !claimed ? (
            <Alert
              status="info"
              title={t("web.communities.show.prelisted_banner.title")}
              description={t("web.communities.show.prelisted_banner.description")}
            >
              <Alert.Action
                label={t("web.communities.show.prelisted_banner.cta")}
                href={`mailto:${support_mailto}?subject=${encodeURIComponent(`Claim ${community.display_name}`)}`}
              />
            </Alert>
          ) : undefined
        }
        celebrations={celebrations}
        community={community}
        onAddCelebration={can.create_celebration ? () => visitModal(newCelebrationPath(community.handle)) : undefined}
        onShareProfile={handleShareProfile}
        onViewFriends={handleViewFriends}
        programs={programs}
        restricted={restricted}
        tabs={
          has_store
            ? { profileHref: profilePath(community.handle), storeHref: profileStorePath(community.handle) }
            : undefined
        }
        {...friendshipActions}
      />
    </PageFrame>
  );
};

export default CommunityShowPage;
